import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NeuronAndAnotherCrmService } from '../../../../../../services/neuron-and-another-crm.service';
import {
  ANOTHER_CRM_TYPE,
  DEFAULT_ERROR,
  ERROR_MESSAGES,
  activeTranslations,
  NEURON_INTEGRATION_STATUS,
  NEURON_INTEGRATION_STATUS_OBJECT,
} from '../../../../../../enum/constants';
import {
  CreateNeuronCrmIntegrationRequest,
  NeuronIntegrationEntity,
  NeuronIntegrationForm,
} from './neuron-and-another-crm.interface';
import { Subject, takeUntil } from 'rxjs';
import { IError } from '../../../../../../interfaces';
import { NotificationService } from '../../../../../../services/notifications.service';

@Component({
  selector: 'app-neuron-and-another-crm',
  templateUrl: './neuron-and-another-crm.component.html',
  styleUrls: ['./neuron-and-another-crm.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class NeuronAndAnotherCrmComponent implements OnInit, OnDestroy {
  constructor(
    protected readonly neuronAndAnotherCrmService: NeuronAndAnotherCrmService,
    protected readonly notificationService: NotificationService,
  ) {}

  @Input() payload: any;
  @Output() close = new EventEmitter<any>();

  protected readonly destroy$ = new Subject<null>();
  protected formGroup!: FormGroup<NeuronIntegrationForm>;
  protected crmControl = new FormControl();
  protected status: NEURON_INTEGRATION_STATUS = NEURON_INTEGRATION_STATUS.CREATED;
  protected integration?: NeuronIntegrationEntity;
  protected isLoader: boolean = false;
  protected selectedCrm: ANOTHER_CRM_TYPE = ANOTHER_CRM_TYPE.UNKNOWN;

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  ngOnInit(): void {
    this.getIntegration();
    this.formGroup = new FormGroup<NeuronIntegrationForm>({
      domainControl: new FormControl('', [Validators.required]),
      apiKeyControl: new FormControl('', [Validators.required]),
    });
  }

  private getIntegration() {
    this.isLoader = true;
    this.neuronAndAnotherCrmService
      .getIntegration()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (integration: NeuronIntegrationEntity) => {
          this.integration = integration;
          if (integration) {
            this.status = integration.status;
            this.selectedCrm = integration.another_crm_type;
          } else {
            this.selectedCrm = ANOTHER_CRM_TYPE.UNKNOWN;
            this.status = NEURON_INTEGRATION_STATUS.DISCONNECTED;
          }
          this.isLoader = false;
        },
        error: (e: any): void => {
          this.isLoader = false;
          (e.error?.errors || []).forEach((err: IError) => {
            this.notificationService.showError(
              activeTranslations.notifications_texts.error.error,
              ERROR_MESSAGES[err.code] || DEFAULT_ERROR,
            );
          });
        },
      });
  }

  protected connect() {
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      return;
    }

    const formValues = this.formGroup.getRawValue();

    const payload: CreateNeuronCrmIntegrationRequest = {
      api_key: formValues.apiKeyControl,
      crm_type: this.crmControl.value,
      domain: formValues.domainControl,
    };
    this.isLoader = true;
    this.neuronAndAnotherCrmService
      .createIntegration(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isLoader = false;
          this.getIntegration();
        },
        error: (e: any): void => {
          this.isLoader = false;
          (e.error?.errors || []).forEach((err: IError) => {
            this.notificationService.showError(
              activeTranslations.notifications_texts.error.error,
              ERROR_MESSAGES[err.code] || DEFAULT_ERROR,
            );
          });
        },
      });
  }

  protected delete() {
    this.isLoader = true;
    this.neuronAndAnotherCrmService
      .deleteIntegration()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isLoader = false;
          this.getIntegration();
        },
        error: (e: any): void => {
          this.isLoader = false;
          (e.error?.errors || []).forEach((err: IError) => {
            this.notificationService.showError(
              activeTranslations.notifications_texts.error.error,
              ERROR_MESSAGES[err.code] || DEFAULT_ERROR,
            );
          });
        },
      });
  }

  protected onSelectCrm() {
    if (this.crmControl.invalid) {
      this.crmControl.markAsTouched();
      return;
    }
    this.selectedCrm = this.crmControl.getRawValue();
  }

  protected copyWebhookUrl() {
    const data = this.neuronAndAnotherCrmService.getWebhookUrl(this.selectedCrm);
    if (!data) {
      this.notificationService.showError(
        '',
        activeTranslations.notifications_texts.error
          .there_was_an_error_please_try_againn,
      );
      return;
    }

    navigator.clipboard
      .writeText(data)
      .then(() => {
        this.notificationService.showSuccess(
          '',
          activeTranslations.notifications_texts.info.copied,
        );
      })
      .catch((_) => {
        this.notificationService.showError(
          '',
          activeTranslations.notifications_texts.error
            .there_was_an_error_please_try_againn,
        );
      });
  }

  protected readonly ANOTHER_CRM_TYPE = ANOTHER_CRM_TYPE;
  protected readonly NEURON_INTEGRATION_STATUS_OBJECT = NEURON_INTEGRATION_STATUS_OBJECT;
  protected readonly NEURON_INTEGRATION_STATUS = NEURON_INTEGRATION_STATUS;
}
